import type { IStorageProvider } from '@unleash/proxy-client-react';

export type ResilientStorageMode = 'local' | 'session';

interface ResilientStorageProviderOptions {
  mode: ResilientStorageMode;
  prefix?: string;
}

// Per-mode memory store so local and session fallbacks don't cross-contaminate
// when `window.localStorage`/`window.sessionStorage` is unavailable.
const memoryStores: Record<ResilientStorageMode, Map<string, string>> = {
  local: new Map(),
  session: new Map(),
};
const storageAvailability: Record<ResilientStorageMode, boolean | undefined> = {
  local: undefined,
  session: undefined,
};

const getBackingStorage = (mode: ResilientStorageMode): Storage =>
  mode === 'session' ? window.sessionStorage : window.localStorage;

const isStorageAvailable = (mode: ResilientStorageMode): boolean => {
  if (storageAvailability[mode] !== undefined) {
    return storageAvailability[mode] as boolean;
  }

  try {
    const testKey = '__resilient_storage_provider_test__';
    const storage = getBackingStorage(mode);
    storage.setItem(testKey, 'test');
    storage.removeItem(testKey);
    storageAvailability[mode] = true;
  } catch {
    storageAvailability[mode] = false;
  }

  return storageAvailability[mode] as boolean;
};

const dispatchStorageEvent = (
  mode: ResilientStorageMode,
  key: string,
  newValue: string | null,
): void => {
  try {
    const storageArea = isStorageAvailable(mode) ? getBackingStorage(mode) : null;
    window.dispatchEvent(new StorageEvent('storage', { key, newValue, storageArea }));
  } catch {
    /* not fatal — listeners just won't be notified */
  }
};

/**
 * Synchronously read the raw JSON string stored at `key`, falling back to the
 * in-process memory store when the backing storage is unavailable. Returns
 * `undefined` when the key is absent. Callers are responsible for parsing retrieved values.
 */
export const readResilientStorageRaw = (
  mode: ResilientStorageMode,
  key: string,
): string | undefined => {
  if (isStorageAvailable(mode)) {
    try {
      const value = getBackingStorage(mode).getItem(key);
      return value === null ? undefined : value;
    } catch {
      /* fall through to memory store */
    }
  }
  return memoryStores[mode].get(key);
};

/**
 * Synchronously read a JSON-serialized value from the backing storage,
 * falling back to an in-process memory store when the storage is unavailable.
 * Returns `undefined` when the key is absent or stored data can't be parsed.
 */
export const readResilientStorage = (mode: ResilientStorageMode, key: string): unknown => {
  const raw = readResilientStorageRaw(mode, key);
  if (raw === undefined) {
    return undefined;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

/**
 * Synchronously write a JSON-serializable value to the backing storage,
 * falling back to an in-process memory store when the storage is unavailable
 * or throws. Dispatches a `storage` event on `window` so subscribed components
 * (e.g. `useResilientStorage`) re-render with the new value.
 */
export const writeResilientStorage = (
  mode: ResilientStorageMode,
  key: string,
  value: unknown,
): void => {
  if (value === undefined || value === null) {
    if (isStorageAvailable(mode)) {
      try {
        getBackingStorage(mode).removeItem(key);
        dispatchStorageEvent(mode, key, null);
        return;
      } catch {
        /* fall through to memory store */
      }
    }
    memoryStores[mode].delete(key);
    dispatchStorageEvent(mode, key, null);
    return;
  }

  const stringified = JSON.stringify(value);
  if (isStorageAvailable(mode)) {
    try {
      getBackingStorage(mode).setItem(key, stringified);
      dispatchStorageEvent(mode, key, stringified);
      return;
    } catch {
      /* fall through to memory store */
    }
  }
  memoryStores[mode].set(key, stringified);
  dispatchStorageEvent(mode, key, stringified);
};

/**
 * Storage provider that uses the browser's localStorage or sessionStorage
 * (selected via the required `mode` option) and falls back to in-memory
 * storage when it's not available (e.g. sandboxed iframes, private browsing
 * restrictions, or environments without `window.localStorage`/`sessionStorage`).
 */
export class ResilientStorageProvider implements IStorageProvider {
  private readonly mode: ResilientStorageMode;
  private readonly prefix: string;

  public constructor({ mode, prefix = '' }: ResilientStorageProviderOptions) {
    this.mode = mode;
    this.prefix = prefix;
  }

  public save(name: string, data: unknown): Promise<void> {
    writeResilientStorage(this.mode, `${this.prefix}${name}`, data);
    return Promise.resolve();
  }

  public get(name: string): Promise<unknown> {
    return Promise.resolve(readResilientStorage(this.mode, `${this.prefix}${name}`));
  }
}
